import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import {
  createAiReputationReport,
  getCommercialContext,
  saveCommercialContext,
} from "../../api/aiReputation";
import { useAuth } from "../../contexts/AuthContext";
import { useUsage } from "../../hooks/useUsage";
import type { CommercialContext, CommercialContextRequest } from "../../types/aiReputation";
import UsageMeter from "../UsageMeter";
import { AiReputationLoaderDialog } from "./AiReputationLoaderDialog";
import { CommercialContextEditor } from "./CommercialContextEditor";

type ContextState = {
  userId: string;
  value: CommercialContext;
};

export const AiReputationEngine = () => {
  const { user, businessProfile } = useAuth();
  const { byFeature, refresh } = useUsage();
  const navigate = useNavigate();
  const [contextState, setContextState] = useState<ContextState | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const context = contextState?.userId === user?.id ? contextState.value : null;
  useEffect(() => {
    const controller = new AbortController();
    if (!user) return () => controller.abort();
    const userId = user.id;
    const loadContext = async () => {
      try {
        const result = await getCommercialContext(controller.signal);
        if (!controller.signal.aborted) setContextState({ userId, value: result.data });
      } catch {
        if (!controller.signal.aborted) setContextState(null);
      }
    };
    void loadContext();
    return () => controller.abort();
  }, [user]);
  const contextReady = context?.is_complete === true;
  const run = async () => {
    if (!user) {
      navigate("?auth=login");
      return;
    }
    if (!businessProfile?.is_complete || !contextReady) return;
    setLoading(true);
    setError(null);
    try {
      const result = await createAiReputationReport();
      window.dispatchEvent(new CustomEvent("aiReputationReportsUpdated"));
      await refresh();
      navigate(`/ai-reputation/report/${result.data.report_id}`);
    } catch (caught: unknown) {
      setError(caught instanceof Error ? caught.message : "Unable to generate the analysis.");
      setLoading(false);
    }
  };
  return (
    <main className="mx-auto max-w-6xl px-6 py-12 space-y-8">
      {loading && <AiReputationLoaderDialog />}
      <section>
        <p className="text-primary font-bold uppercase text-sm">AI Reputation</p>
        <h1 className="font-headline text-5xl font-extrabold">
          Who is shaping your AI reputation?
        </h1>
        <p className="mt-4 max-w-3xl text-lg text-on-surface-variant">
          Sample web-grounded answers, observe visible sources, and review the commercial context
          used for your analysis.
        </p>
      </section>
      <section className="rounded-2xl border border-surface-container-high p-6">
        <h2 className="font-bold text-xl">Readiness</h2>
        <p>Business profile: {businessProfile?.is_complete ? "ready" : "needs completion"}</p>
        <p>
          Commercial context:{" "}
          {contextReady ? "ready" : "complete all required strategy areas below"}
        </p>
        <button
          type="button"
          disabled={loading || !user || !businessProfile?.is_complete || !contextReady}
          onClick={run}
          className="mt-4 rounded-xl bg-primary px-5 py-3 font-bold text-on-primary disabled:opacity-50"
        >
          {user ? "Run AI reputation analysis" : "Log in to analyze"}
        </button>
        {error && (
          <p role="alert" className="mt-3 text-error">
            {error}
          </p>
        )}
        {user && (
          <UsageMeter
            item={byFeature?.ai_reputation_reports ?? byFeature?.prompt_tests}
            className="mt-4"
          />
        )}
      </section>
      <CommercialContextEditor
        context={context}
        onSave={async (next: CommercialContextRequest) => {
          const result = await saveCommercialContext(next);
          if (user) setContextState({ userId: user.id, value: result.data });
        }}
        onReload={async () => {
          try {
            const result = await getCommercialContext();
            if (user) setContextState({ userId: user.id, value: result.data });
          } catch (caught: unknown) {
            setError(
              caught instanceof Error ? caught.message : "Unable to reload the commercial context.",
            );
          }
        }}
      />
    </main>
  );
};
